// src/services/api.js
import axios from "axios";
import { getToken, getRole, setAuth, clearAuth } from "./authStorage";

// Use a configurable API base so the same build can work behind the Lightsail
// reverse proxy (`/api`) or against a separately hosted backend.
const API_PREFIX = process.env.REACT_APP_API_BASE || "/api";

/* =========================
   Axios instance
========================= */
const http = axios.create({
  baseURL: API_PREFIX,
  timeout: 120000,
});

// Attach bearer token automatically
http.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Unwrap data + normalize errors (and clear auth on 401)
http.interceptors.response.use(
  (res) => res.data,
  (err) => {
    if (err?.response?.status === 401) {
      clearAuth();
    }
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.msg ||
      err?.response?.data?.error ||
      err?.message ||
      "Request failed";
    return Promise.reject(new Error(message));
  }
);

/* =========================
   Small helper
========================= */
const refreshRoleFromMe = async () => {
  try {
    const me = await http.get(`/auth/me`);
    const role = me?.role || me?.user?.role || null;
    if (role) {
      const token = getToken();
      setAuth({ token, role });
    }
    return me;
  } catch {
    return null;
  }
};

/* =========================
   Auth API
========================= */
export const sendOtp = (contact) => http.post(`/auth/send-otp`, { contact });

export const verifyOtp = async ({ contact, otp, password }) => {
  const data = await http.post(`/auth/verify-otp`, { contact, otp, password });

  if (data?.token) {
    // Prefer role from response; otherwise pull from /me
    const role = data?.user?.role || data?.role || null;
    setAuth({ token: data.token, role: role || getRole() || "" });
    if (!role) await refreshRoleFromMe();
  }

  return data;
};

export const login = async ({ username, password }) => {
  const data = await http.post(`/auth/login`, { username, password });

  if (data?.token) {
    const role = data?.user?.role || data?.role || null;
    setAuth({ token: data.token, role: role || getRole() || "" });
    if (!role) await refreshRoleFromMe();
  }

  return data;
};

export const requestPasswordReset = (payload) => {
  const body =
    typeof payload === "string"
      ? { identifier: payload }
      : { ...(payload || {}) };

  if (!body.identifier) {
    body.identifier = body.email || body.username || body.contact || "";
  }

  return http.post(`/auth/request-password-reset`, body);
};

export const resetPassword = ({ token, password }) =>
  http.post(`/auth/reset-password`, { token, password });

export const updateProfile = (payload = {}) =>
  http.patch(`/auth/profile`, payload);

export const changePassword = ({ currentPassword, newPassword }) =>
  http.post(`/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const fetchCurrentUser = () =>
  http.get(`/auth/me`, {
    params: { _: Date.now() },
    headers: { "Cache-Control": "no-cache" },
  });
export const logout = () => clearAuth();

/* =========================
   Onboarding / Documents
========================= */
export const uploadDocument = (fileOrFormData, arg2) => {
  let docType = typeof arg2 === "string" ? arg2 : null;
  const extraConfig =
    arg2 && typeof arg2 === "object" && !(arg2 instanceof FormData) ? arg2 : {};

  let formData;
  if (fileOrFormData instanceof FormData) {
    formData = fileOrFormData;
  } else {
    formData = new FormData();
    formData.append("file", fileOrFormData);
  }

  const explicitDocType = formData.get("doc_type") || formData.get("document_type");
  const effectiveDocType = docType || explicitDocType || extraConfig?.docType;
  if (effectiveDocType && !explicitDocType) {
    formData.append("doc_type", effectiveDocType);
    formData.append("document_type", effectiveDocType);
  }

  return http.post(`/auth/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(extraConfig.headers || {}),
    },
    ...extraConfig,
  });
};

export const finalizeOnboarding = (userId) =>
  http.post(`/auth/finalize-onboarding`, { user_id: userId });

export const submitOnboardingDetails = (payload) =>
  http.post(`/onboarding/submit`, payload);

/* =========================
   Wallet / Transactions
========================= */
export const deposit = (amount, currency, note) =>
  http.post(`/transactions/deposit`, { amount, currency, note });

export const withdraw = (amount, note) =>
  http.post(`/transactions/withdraw`, { amount, note });

export const transfer = (amount, to, note, idempotency_key) =>
  http.post(`/transactions/transfer`, { amount, to, note, idempotency_key });

export const getHistory = () => http.get(`/transactions/history`);
export const getWalletSummary = () => http.get(`/transactions/wallet-summary`);
export const getDepositInstructions = () =>
  http.get(`/transactions/deposit/instructions`);
export const downloadStatement = (params = {}) =>
  http.get(`/transactions/statement`, { params, responseType: "blob" });

/* =========================
   Investments (User)
========================= */
export const getInvestmentOpportunities = () =>
  http.get(`/investments/opportunities`);

export const purchaseInvestment = (payload) =>
  http.post(`/investments/purchase`, payload);

export const getMyInvestments = () => http.get(`/investments/my`);

export const getInvestmentSchedule = (investmentId) =>
  http.get(`/investments/my/${investmentId}/schedule`);
export const topUpInvestment = ({ investmentId, amount, note }) =>
  http.post(`/investments/top-up`, { investment_id: investmentId, amount, note });

export const liquidateInvestment = ({ investmentId, amount, note, liquidationType }) =>
  http.post(`/investments/liquidate`, {
    investment_id: investmentId,
    amount,
    note,
    liquidation_type: liquidationType,
  });

export const simulateOpportunity = (slug, body) =>
  http.post(`/investments/opportunities/${slug}/simulate`, body);

export const getDailyInvestmentPerformance = (params = {}) =>
  http.get(`/investments/my/performance/daily`, { params });

/* =========================
   Investments (Admin)
========================= */
export const adminListOpportunities = () =>
  http.get(`/investments/admin/opportunities`);

export const adminCreateOpportunity = (body) =>
  http.post(`/investments/admin/opportunities`, body);

export const adminUpdateOpportunity = (slug, body) =>
  http.put(`/investments/admin/opportunities/${slug}`, body);

export const adminDeleteOpportunity = (slug) =>
  http.delete(`/investments/admin/opportunities/${slug}`);

export const adminSimulateOpportunity = (slug, body) =>
  http.post(`/investments/admin/opportunities/${slug}/simulate`, body);

export const adminListInvestmentSubscriptions = (params = {}) =>
  http.get(`/admin/investments/subscriptions`, { params });

export const adminGetInvestmentSubscription = (investmentId) =>
  http.get(`/admin/investments/subscriptions/${investmentId}`);

export const adminListInvestmentCashflows = (params = {}) =>
  http.get(`/admin/investments/cashflows`, { params });

export const adminLiquidateInvestment = (investmentId, body = {}) =>
  http.post(`/admin/investments/subscriptions/${investmentId}/liquidate`, body);

export const adminGetClient360 = (params = {}) =>
  http.get(`/admin/finance/client-360`, { params });

/* =========================
   Admin
========================= */
export const adminListPendingUsers = () =>
  http.get(`/admin/pending-users`);

export const adminCreateManualPendingUser = (formData) =>
  http.post(`/admin/compliance/manual-user`, formData, {
    timeout: 300000,
  });

export const adminCreateStaffUser = (payload = {}) =>
  http.post(`/admin/compliance/staff-users`, payload);

export const adminGetPendingUserDetail = (pendingId) =>
  http.get(`/admin/pending-users/${pendingId}`);

export const adminUpdatePendingNote = (pendingId, note) =>
  http.post(`/admin/pending-users/${pendingId}/note`, { note });

export const adminApprovePendingUser = (pendingId, body = {}) =>
  http.post(`/admin/approve-user/${pendingId}`, body);

export const adminRejectPendingUser = (pendingId, body = {}) =>
  http.post(`/admin/reject-user/${pendingId}`, body);

export const adminFetchMetrics = () => http.get(`/admin/metrics`);
export const adminFetchDepartmentDashboard = (department) =>
  http.get(`/admin/dashboard/${department}`);

export const adminListApprovedUsers = (params = {}) =>
  http.get(`/admin/approved-users`, { params });

export const adminGetApprovedUserDetail = (userId) =>
  http.get(`/admin/approved-users/${userId}`);

export const adminUpdateApprovedUser = (userId, body = {}) =>
  http.patch(`/admin/approved-users/${userId}`, body);

export const adminUploadApprovedUserDocument = (userId, formData) =>
  http.post(`/admin/approved-users/${userId}/documents`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });

export const adminListCompanyTransactions = (params = {}) =>
  http.get(`/admin/company-transactions`, { params });

export const adminCreateCompanyTransaction = (payload) =>
  http.post(`/admin/company-transactions`, payload);

export const adminDeleteCompanyTransaction = (id) =>
  http.delete(`/admin/company-transactions/${id}`);

export const adminFetchLedgerEntries = (params = {}) =>
  http.get(`/admin/ledger/entries`, { params });
export const adminExportLedgerEntries = (params = {}) =>
  http.get(`/admin/ledger/entries/export`, { params, responseType: "blob" });
export const adminFetchFinanceReport = (params = {}) =>
  http.get(`/admin/reports/finance`, { params });
export const adminExportFinanceReport = (params = {}) =>
  http.get(`/admin/reports/finance/export`, { params, responseType: "blob" });

export const adminListPendingWalletTransactions = () =>
  http.get(`/admin/pending-transactions`);
export const adminExportPendingWalletTransactions = (params = {}) =>
  http.get(`/admin/pending-transactions/export`, { params, responseType: "blob" });

export const adminApprovePendingWalletTransaction = (id, body = {}) =>
  http.post(`/admin/pending-transactions/${id}/approve`, body);

export const adminRejectPendingWalletTransaction = (id, body = {}) =>
  http.post(`/admin/pending-transactions/${id}/reject`, body);

export const adminListBackofficePostings = (params = {}) =>
  http.get(`/admin/backoffice/postings`, { params });

export const adminListBackofficeClients = (params = {}) =>
  http.get(`/admin/backoffice/clients`, { params });

export const adminListFinanceClients = (params = {}) =>
  http.get(`/admin/finance/clients`, { params });

export const adminGetBackofficeClient = (userId) =>
  http.get(`/admin/backoffice/clients/${userId}`);

export const adminCreateBackofficePosting = (payload) =>
  http.post(`/admin/backoffice/postings`, payload);

export const adminSubmitBackofficePosting = (postingId, payload) =>
  http.post(`/admin/backoffice/postings/${postingId}/submit`, payload);

export const adminApproveBackofficePosting = (postingId, body = {}) =>
  http.post(`/admin/backoffice/postings/${postingId}/approve`, body);

export const adminRejectBackofficePosting = (postingId, body = {}) =>
  http.post(`/admin/backoffice/postings/${postingId}/reject`, body);

export const adminExportBackofficePostings = (params = {}) =>
  http.get(`/admin/backoffice/postings/export`, { params, responseType: "blob" });

export const adminListLedgerAccounts = (params = {}) =>
  http.get(`/admin/ledger/accounts`, { params });
export const adminListChartOfAccounts = (params = {}) =>
  http.get(`/admin/chart-of-accounts`, { params });
export const adminCreateLedgerAccount = (payload = {}) =>
  http.post(`/admin/ledger/accounts`, payload);
export const adminUpdateLedgerAccount = (id, payload = {}) =>
  http.patch(`/admin/ledger/accounts/${id}`, payload);

export const adminListBankAccounts = (params = {}) =>
  http.get(`/admin/bank-accounts`, { params });

export const adminCreateBankAccount = (payload = {}) =>
  http.post(`/admin/bank-accounts`, payload);

export const adminUpdateBankAccount = (accountId, payload = {}) =>
  http.patch(`/admin/bank-accounts/${accountId}`, payload);

/* =========================
   Admin: KYC
========================= */
export const approveUserKyc = (userId) =>
  http.post(`/admin/users/${userId}/kyc/approve`);

/* =========================
   Firm Investment Management
========================= */
export const firmListProducts = (params = {}) =>
  http.get(`/firm/products`, { params });
export const firmCreateProduct = (payload = {}) =>
  http.post(`/firm/products`, payload);
export const firmListInvestments = (params = {}) =>
  http.get(`/firm/investments`, { params });
export const firmCreateInvestment = (payload = {}) =>
  http.post(`/firm/investments`, payload);
export const firmGetInvestment = (id) =>
  http.get(`/firm/investments/${id}`);
export const firmSubmitInvestment = (id) =>
  http.post(`/firm/investments/${id}/submit`);
export const firmApproveInvestment = (id, payload = {}) =>
  http.post(`/firm/investments/${id}/approve`, payload);
export const firmSettleInvestment = (id, payload = {}) =>
  http.post(`/firm/investments/${id}/settle`, payload);
export const firmRejectInvestment = (id, payload = {}) =>
  http.post(`/firm/investments/${id}/reject`, payload);
export const firmCloseInvestment = (id) =>
  http.post(`/firm/investments/${id}/close`);
export const firmListAllocations = (investmentId) =>
  http.get(`/firm/investments/${investmentId}/allocations`);
export const firmCreateAllocation = (investmentId, payload = {}) =>
  http.post(`/firm/investments/${investmentId}/allocations`, payload);
export const firmReallocateAllocation = (allocationId, payload = {}) =>
  http.post(`/firm/allocations/${allocationId}/reallocate`, payload);
export const firmReportAum = (params = {}) =>
  http.get(`/firm/reports/aum`, { params });
export const firmReportMaturityLadder = (params = {}) =>
  http.get(`/firm/reports/maturity-ladder`, { params });
export const firmReportCashflows = (params = {}) =>
  http.get(`/firm/reports/cashflows`, { params });
export const firmRunAccruals = (payload = {}) =>
  http.post(`/firm/jobs/accruals`, payload);

/* =========================
   Utils
========================= */
export const health = () => http.get(`/health`);

/* =========================
   Default export (everything you call as api.x)
========================= */
const api = {
  // auth
  sendOtp,
  verifyOtp,
  login,
  requestPasswordReset,
  resetPassword,
  updateProfile,
  changePassword,
  fetchCurrentUser,
  logout,

  // onboarding
  uploadDocument,
  finalizeOnboarding,
  submitOnboardingDetails,

  // wallet
  deposit,
  withdraw,
  transfer,
  getHistory,
  getWalletSummary,
  getDepositInstructions,
  downloadStatement,

  // investments (user)
  getInvestmentOpportunities,
  purchaseInvestment,
  getMyInvestments,
  getInvestmentSchedule,
  topUpInvestment,
  liquidateInvestment,
  simulateOpportunity,
  getDailyInvestmentPerformance,

  // investments (admin)
  adminListOpportunities,
  adminCreateOpportunity,
  adminUpdateOpportunity,
  adminDeleteOpportunity,
  adminSimulateOpportunity,
  adminListInvestmentSubscriptions,
  adminGetInvestmentSubscription,
  adminListInvestmentCashflows,
  adminLiquidateInvestment,
  adminGetClient360,

  // admin
  adminListPendingUsers,
  adminCreateManualPendingUser,
  adminCreateStaffUser,
  adminGetPendingUserDetail,
  adminUpdatePendingNote,
  adminApprovePendingUser,
  adminRejectPendingUser,
  adminFetchMetrics,
  adminFetchDepartmentDashboard,
  adminListApprovedUsers,
  adminGetApprovedUserDetail,
  adminUpdateApprovedUser,
  adminListCompanyTransactions,
  adminCreateCompanyTransaction,
  adminDeleteCompanyTransaction,
  adminFetchLedgerEntries,
  adminExportLedgerEntries,
  adminExportFinanceReport,
  adminFetchFinanceReport,
  adminListPendingWalletTransactions,
  adminExportPendingWalletTransactions,
  adminApprovePendingWalletTransaction,
  adminRejectPendingWalletTransaction,
  adminListBackofficePostings,
  adminListBackofficeClients,
  adminListFinanceClients,
  adminGetBackofficeClient,
  adminCreateBackofficePosting,
  adminSubmitBackofficePosting,
  adminApproveBackofficePosting,
  adminRejectBackofficePosting,
  adminExportBackofficePostings,
  adminListLedgerAccounts,
  adminListChartOfAccounts,
  adminCreateLedgerAccount,
  adminUpdateLedgerAccount,
  adminListBankAccounts,
  adminCreateBankAccount,
  adminUpdateBankAccount,
  approveUserKyc,

  firmListProducts,
  firmCreateProduct,
  firmListInvestments,
  firmCreateInvestment,
  firmGetInvestment,
  firmSubmitInvestment,
  firmApproveInvestment,
  firmSettleInvestment,
  firmRejectInvestment,
  firmCloseInvestment,
  firmListAllocations,
  firmCreateAllocation,
  firmReallocateAllocation,
  firmReportAum,
  firmReportMaturityLadder,
  firmReportCashflows,
  firmRunAccruals,

  // utils
  health,
};

export default api;

// Optional re-exports for places that import the primitives
export { getToken, getRole, setAuth, clearAuth };
// Expose the raw axios instance for modules that need direct POST/GET (e.g., fixed income API)
export { http as httpClient };
export { API_PREFIX as API_BASE };
